/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useMemo, useCallback } from 'react';

// ==============================================================================
// Thai Date & Fiscal Year Helpers
// ==============================================================================
export const THAI_MONTHS_SHORT = [
  'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
  'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'
];

export const THAI_MONTHS_FULL = [
  'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
  'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
];

/**
 * Formats a Date or date string to Thai format e.g. "15 ต.ค. 2568"
 */
export const formatThaiDate = (dateVal) => {
  if (!dateVal) return '-';
  const d = new Date(dateVal);
  if (isNaN(d.getTime())) return '-';
  const day = d.getDate();
  const month = THAI_MONTHS_SHORT[d.getMonth()];
  const year = d.getFullYear() + 543;
  return `${day} ${month} ${year}`;
};

/**
 * Calculates current Thai fiscal year (CE year + 543; if month >= Oct, fiscal year is next CE year)
 */
export const getCurrentFiscalYear = (now = new Date()) => {
  const ceYear = now.getFullYear();
  const month = now.getMonth(); // 0 = Jan, 8 = Sep, 9 = Oct
  const fiscalCe = month >= 9 ? ceYear + 1 : ceYear;
  return fiscalCe + 543;
};

/**
 * Calculates period range for Fiscal Year (1 ต.ค. ปีก่อนหน้า ถึง 30 ก.ย. ปีงบประมาณ)
 */
export const getFiscalYearRange = (fiscalYearBE) => {
  const endCeYear = fiscalYearBE - 543;
  const startCeYear = endCeYear - 1;
  const startDate = new Date(Date.UTC(startCeYear, 9, 1, 0, 0, 0, 0)); // Oct 1
  const endDate = new Date(Date.UTC(endCeYear, 8, 30, 23, 59, 59, 999)); // Sep 30
  const beginningDateThai = `1 ต.ค. ${fiscalYearBE - 1}`;
  const periodLabel = `ปีงบประมาณ ${fiscalYearBE} (1 ต.ค. ${fiscalYearBE - 1} - 30 ก.ย. ${fiscalYearBE})`;

  return {
    startDate,
    endDate,
    beginningDateThai,
    periodLabel,
    yearSuffix: `${fiscalYearBE}`
  };
};

/**
 * Calculates period range for a specific Month
 */
export const getMonthRange = (month1Based, yearBE) => {
  const ceYear = yearBE - 543;
  const monthIndex = month1Based - 1;
  const startDate = new Date(Date.UTC(ceYear, monthIndex, 1, 0, 0, 0, 0));
  const endDate = new Date(Date.UTC(ceYear, monthIndex + 1, 0, 23, 59, 59, 999));
  const lastDay = endDate.getUTCDate();
  const monthShort = THAI_MONTHS_SHORT[monthIndex];
  const monthFull = THAI_MONTHS_FULL[monthIndex];
  const beginningDateThai = `1 ${monthShort} ${yearBE}`;
  const periodLabel = `เดือน${monthFull} ${yearBE} (1 - ${lastDay} ${monthShort} ${yearBE})`;

  return {
    startDate,
    endDate,
    beginningDateThai,
    periodLabel,
    yearSuffix: `${yearBE}_${String(month1Based).padStart(2, '0')}`
  };
};

// ==============================================================================
// CSV Generation Helper (with UTF-8 BOM)
// ==============================================================================
export const generateMaterialLedgerCSV = ({
  item,
  periodLabel,
  beginningDateThai,
  beginningBalance = 0,
  isMonthly = false,
  ledgerRows = [],
  totalReceived = 0,
  totalDispensed = 0,
  endingBalance = 0,
  pageNumber = 1
}) => {
  const escapeCsv = (val) => {
    if (val === null || val === undefined) return '';
    const str = String(val);
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const category = item?.category || 'วัสดุสิ้นเปลือง';
  const itemName = item?.item_name || '-';
  const unitSize = item?.unit_size || item?.description || '-';
  const unit = item?.unit || '-';
  const storage = [
    item?.cabinet ? `ตู้ ${item.cabinet}` : '',
    item?.shelf ? `ชั้น ${item.shelf}` : '',
    item?.row ? `แถว ${item.row}` : ''
  ].filter(Boolean).join(' ') || item?.location || item?.storage_condition || '-';
  const code = item?.barcode || item?.udi_di || (item?.id ? String(item.id).substring(0, 8) : '-');
  const maxStock = item?.global_min_stock
    ? Number(item.global_min_stock) * 3
    : (item?.min_stock ? Number(item.min_stock) * 3 : 0);
  const minStock = Number(item?.min_stock ?? item?.global_min_stock ?? 0);

  const lines = [];

  // Sheet Title
  lines.push('บัญชีวัสดุ');
  lines.push('');

  // Header Details
  lines.push([escapeCsv('แผ่นที่ (1)'), escapeCsv(pageNumber), escapeCsv('ส่วนราชการ'), escapeCsv('โรงพยาบาลฝาง')].join(','));
  lines.push([escapeCsv('ประเภท (3)'), escapeCsv(category), escapeCsv('หน่วยงาน (2)'), escapeCsv('กลุ่มงานทันตกรรม')].join(','));
  lines.push([escapeCsv('ชื่อหรือชนิดวัสดุ (4)'), escapeCsv(itemName), escapeCsv('รหัส (5)'), escapeCsv(code)].join(','));
  lines.push([escapeCsv('ขนาดหรือลักษณะ'), escapeCsv(unitSize), escapeCsv('จำนวนอย่างสูง (6)'), escapeCsv(maxStock)].join(','));
  lines.push([escapeCsv('หน่วยที่นับ (6)'), escapeCsv(unit), escapeCsv('จำนวนอย่างต่ำ (7)'), escapeCsv(minStock)].join(','));
  lines.push([escapeCsv('ที่เก็บ'), escapeCsv(storage), escapeCsv('รอบระยะเวลา'), escapeCsv(periodLabel)].join(','));
  lines.push('');

  // Table Column Headers
  lines.push([
    escapeCsv('วัน เดือน ปี'),
    escapeCsv('รับจาก/จ่ายให้'),
    escapeCsv('เลขที่เอกสาร (8)'),
    escapeCsv('ราคาต่อหน่วย บาท'),
    escapeCsv('จำนวน รับ'),
    escapeCsv('จำนวน จ่าย'),
    escapeCsv('จำนวน คงเหลือ'),
    escapeCsv('หมายเหตุ')
  ].join(','));

  // Row 1: ยอดยกมา
  lines.push([
    escapeCsv(beginningDateThai),
    escapeCsv('ยอดยกมา'),
    escapeCsv('-'),
    escapeCsv('-'),
    escapeCsv('-'),
    escapeCsv('-'),
    escapeCsv(beginningBalance),
    escapeCsv(isMonthly ? 'ยอดยกมาจากเดือนก่อนหน้า' : 'ยอดยกมาจากปีก่อนหน้า')
  ].join(','));

  // Subsequent transaction rows
  ledgerRows.forEach(r => {
    lines.push([
      escapeCsv(formatThaiDate(r.date)),
      escapeCsv(r.actorOrTarget || '-'),
      escapeCsv(r.docNumber || '-'),
      escapeCsv(r.unitPrice !== null && r.unitPrice !== undefined ? r.unitPrice : '-'),
      escapeCsv(r.inQty !== null && r.inQty !== undefined ? r.inQty : '-'),
      escapeCsv(r.outQty !== null && r.outQty !== undefined ? r.outQty : '-'),
      escapeCsv(r.runningBalance),
      escapeCsv(r.note || '-')
    ].join(','));
  });

  // Summary row
  lines.push([
    escapeCsv('รวมทั้งสิ้น'),
    escapeCsv('-'),
    escapeCsv('-'),
    escapeCsv('-'),
    escapeCsv(totalReceived),
    escapeCsv(totalDispensed),
    escapeCsv(endingBalance),
    escapeCsv('คงเหลือยกไป')
  ].join(','));

  // Prepend UTF-8 BOM
  return '\uFEFF' + lines.join('\r\n');
};

// ==============================================================================
// Main Component: MaterialLedgerSheet
// ==============================================================================
export const MaterialLedgerSheet = ({
  item,
  itemMaster = [],
  onSelectItem = () => {},
  supabase = null,
  supplies = [],
  user = null,
  initialFiscalYear = null
}) => {
  // Current active item state
  const [selectedItemState, setSelectedItemState] = useState(item || itemMaster?.[0] || null);

  useEffect(() => {
    if (item) {
      setSelectedItemState(item);
    }
  }, [item]);

  const currentItem = selectedItemState || item || itemMaster?.[0] || null;

  // Header & Controls State
  const [pageNumber, setPageNumber] = useState(1);
  const [periodMode, setPeriodMode] = useState('fiscal'); // 'fiscal' | 'monthly'

  const currentFY = useMemo(() => getCurrentFiscalYear(), []);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState(initialFiscalYear || currentFY);

  const today = useMemo(() => new Date(), []);
  const [selectedMonth, setSelectedMonth] = useState(today.getMonth() + 1);
  const [selectedCalendarYear, setSelectedCalendarYear] = useState(today.getFullYear() + 543);

  // Manual Beginning Balance Override (optional user customization)
  const [customBeginningBalance, setCustomBeginningBalance] = useState('');

  // Data Loading States
  const [inboundTransactions, setInboundTransactions] = useState([]);
  const [outboundTransactions, setOutboundTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Available fiscal years list (currentFY + 1 down to past 4 years)
  const fiscalYearOptions = useMemo(() => {
    const list = [currentFY + 1, currentFY, currentFY - 1, currentFY - 2, 2569, 2568, 2567];
    return Array.from(new Set(list)).sort((a, b) => b - a);
  }, [currentFY]);

  // Available calendar years for monthly selector
  const calendarYearOptions = useMemo(() => {
    const list = [currentFY + 1, currentFY, currentFY - 1, currentFY - 2, 2569, 2568, 2567];
    return Array.from(new Set(list)).sort((a, b) => b - a);
  }, [currentFY]);

  // Consumable items for selection dropdown
  const consumableItemOptions = useMemo(() => {
    if (!itemMaster || itemMaster.length === 0) return [];
    // Prioritize consumable items if category exists
    const consumables = itemMaster.filter(m => !m.category || m.category === 'วัสดุสิ้นเปลือง' || m.category.includes('สิ้นเปลือง'));
    return consumables.length > 0 ? consumables : itemMaster;
  }, [itemMaster]);

  // Computed Period Range
  const periodInfo = useMemo(() => {
    if (periodMode === 'fiscal') {
      return getFiscalYearRange(selectedFiscalYear);
    }
    return getMonthRange(selectedMonth, selectedCalendarYear);
  }, [periodMode, selectedFiscalYear, selectedMonth, selectedCalendarYear]);

  // ==============================================================================
  // Fetch Inbound & Outbound Data from Supabase
  // ==============================================================================
  const fetchData = useCallback(async () => {
    if (!supabase || !currentItem?.item_name) {
      setInboundTransactions([]);
      setOutboundTransactions([]);
      return;
    }

    setIsLoading(true);
    const itemName = currentItem.item_name.trim();

    try {
      // 1. Fetch Receipts & Receipt Items (Inbound)
      let inbounds = [];
      try {
        // Try joined query first
        const { data: riData, error: riErr } = await supabase
          .from('receipt_items')
          .select(`
            id,
            receipt_id,
            master_id,
            item_name,
            lot_number,
            exp_date,
            quantity,
            unit,
            unit_price,
            total_price,
            warehouse_name,
            created_at,
            receipts (
              id,
              receipt_number,
              po_number,
              supplier_name,
              received_date,
              created_at
            )
          `)
          .ilike('item_name', itemName);

        if (!riErr && Array.isArray(riData)) {
          inbounds = riData.map(ri => {
            const rc = ri.receipts || {};
            const dateStr = rc.received_date || ri.created_at || rc.created_at || new Date().toISOString();
            return {
              id: `ri-${ri.id}`,
              date: new Date(dateStr),
              dateStr,
              type: 'inbound',
              actorOrTarget: rc.supplier_name ? `รับจาก: ${rc.supplier_name}` : 'รับจาก: ผู้ขาย/บริษัท',
              docNumber: rc.receipt_number || rc.po_number || '-',
              unitPrice: Number(ri.unit_price) || Number(currentItem.price_per_unit) || 0,
              inQty: Number(ri.quantity) || 0,
              outQty: null,
              note: ri.lot_number ? `Lot: ${ri.lot_number}` : (ri.exp_date ? `Exp: ${ri.exp_date}` : '')
            };
          });
        } else {
          // Fallback: fetch receipt_items directly without join
          const { data: rawItems } = await supabase
            .from('receipt_items')
            .select('*')
            .ilike('item_name', itemName);

          if (Array.isArray(rawItems) && rawItems.length > 0) {
            const receiptIds = Array.from(new Set(rawItems.map(r => r.receipt_id).filter(Boolean)));
            let receiptsMap = {};
            if (receiptIds.length > 0) {
              const { data: rawReceipts } = await supabase
                .from('receipts')
                .select('id, receipt_number, po_number, supplier_name, received_date, created_at')
                .in('id', receiptIds);
              if (Array.isArray(rawReceipts)) {
                rawReceipts.forEach(r => { receiptsMap[r.id] = r; });
              }
            }

            inbounds = rawItems.map(ri => {
              const rc = receiptsMap[ri.receipt_id] || {};
              const dateStr = rc.received_date || ri.created_at || rc.created_at || new Date().toISOString();
              return {
                id: `ri-${ri.id}`,
                date: new Date(dateStr),
                dateStr,
                type: 'inbound',
                actorOrTarget: rc.supplier_name ? `รับจาก: ${rc.supplier_name}` : 'รับจาก: ผู้ขาย/บริษัท',
                docNumber: rc.receipt_number || rc.po_number || '-',
                unitPrice: Number(ri.unit_price) || Number(currentItem.price_per_unit) || 0,
                inQty: Number(ri.quantity) || 0,
                outQty: null,
                note: ri.lot_number ? `Lot: ${ri.lot_number}` : ''
              };
            });
          }
        }
      } catch (e) {
        console.warn('MaterialLedgerSheet: receipt_items fetch exception', e);
      }

      // 2. Fetch Logs (Outbounds & Inbounds logged directly)
      let outbounds = [];
      try {
        const { data: logsData } = await supabase
          .from('logs')
          .select('*')
          .ilike('item_name', itemName)
          .order('timestamp', { ascending: true });

        if (Array.isArray(logsData)) {
          logsData.forEach(log => {
            const action = log.action || '';
            const timestamp = log.timestamp || log.created_at || new Date().toISOString();
            const dateObj = new Date(timestamp);

            // Inbound via log (if receipt_items was empty or this was a standalone stock-in)
            if (action.includes('รับเข้า') && inbounds.length === 0) {
              const qtyMatch = action.match(/จำนวน\s*([\d,.]+)/);
              const qty = qtyMatch ? parseFloat(qtyMatch[1].replace(/,/g, '')) : 1;
              const docMatch = action.match(/ใบเสร็จ\s*[#:]?\s*([^\s,()[\]]+)/);
              const lotMatch = action.match(/Lot:\s*([^\s,()[\]]+)/i);

              inbounds.push({
                id: `log-in-${log.id}`,
                date: dateObj,
                dateStr: timestamp,
                type: 'inbound',
                actorOrTarget: `รับจาก: ${log.warehouse || 'คลังใหญ่ (คลังพัสดุ)'}`,
                docNumber: docMatch ? docMatch[1] : (log.id ? `LOG-${String(log.id).substring(0, 6)}` : '-'),
                unitPrice: Number(currentItem.price_per_unit) || 0,
                inQty: qty,
                outQty: null,
                note: lotMatch ? `Lot: ${lotMatch[1]}` : ''
              });
            }

            // Outbound / Transfer / Dispense
            if (action.includes('โอนย้าย') || action.includes('เบิกออก') || action.includes('จ่ายออก') || action.includes('จ่าย')) {
              let destRoom = 'ห้องคลีนิก';
              const toMatch = action.match(/ไปยัง\s+(.+?)(?:\s+จำนวน|\s+โดย|\s*\[|\s*\(|$)/);
              if (toMatch) {
                destRoom = toMatch[1].trim();
              } else if (log.warehouse) {
                destRoom = log.warehouse;
              }

              const qtyMatch = action.match(/จำนวน\s*([\d,.]+)/);
              const qty = qtyMatch ? parseFloat(qtyMatch[1].replace(/,/g, '')) : 1;
              const docMatch = action.match(/(?:เลขที่|ใบเบิก|Order|Ref|อ้างอิง)\s*[:#]?\s*([^\s,()[\]]+)/i);
              const lotMatch = action.match(/\(Lot:\s*([^)]+)\)/i) || action.match(/Lot:\s*([^\s,()[\]]+)/i);
              const noteMatch = action.match(/\[หมายเหตุ:\s*([^\]]+)\]/);

              outbounds.push({
                id: `log-out-${log.id || Math.random()}`,
                date: dateObj,
                dateStr: timestamp,
                type: 'outbound',
                actorOrTarget: `จ่ายให้: ${destRoom}`,
                docNumber: docMatch ? docMatch[1] : (log.id ? `LOG-${String(log.id).substring(0, 6)}` : '-'),
                unitPrice: Number(currentItem.price_per_unit) || 0,
                inQty: null,
                outQty: qty,
                note: [lotMatch ? `Lot: ${lotMatch[1]}` : '', noteMatch ? noteMatch[1] : ''].filter(Boolean).join(' ')
              });
            }
          });
        }
      } catch (e) {
        console.warn('MaterialLedgerSheet: logs fetch exception', e);
      }

      // 3. Fetch Orders (Completed dispenses)
      try {
        const { data: ordersData } = await supabase
          .from('orders')
          .select('*')
          .order('timestamp', { ascending: true });

        if (Array.isArray(ordersData)) {
          ordersData.forEach(order => {
            if (order.status !== 'completed') return;
            const items = Array.isArray(order.items) ? order.items : [];
            const timestamp = order.timestamp || order.created_at || new Date().toISOString();
            const dateObj = new Date(timestamp);
            const dest = order.clinic_name || 'ห้องคลีนิก';
            const docNo = order.order_number || (order.id ? `ORD-${String(order.id).substring(0, 6)}` : '-');

            items.forEach((it, idx) => {
              const itName = (it.name || it.item_name || '').trim().toLowerCase();
              if (itName === itemName.toLowerCase() || it.master_id === currentItem.id) {
                const qty = Number(it.quantity || it.qty || it.item_amount || 1);
                outbounds.push({
                  id: `order-out-${order.id}-${idx}`,
                  date: dateObj,
                  dateStr: timestamp,
                  type: 'outbound',
                  actorOrTarget: `จ่ายให้: ${dest}`,
                  docNumber: docNo,
                  unitPrice: Number(it.price_per_unit || currentItem.price_per_unit) || 0,
                  inQty: null,
                  outQty: qty,
                  note: it.lot_number ? `Lot: ${it.lot_number}` : (order.note || '')
                });
              }
            });
          });
        }
      } catch (e) {
        console.warn('MaterialLedgerSheet: orders fetch exception', e);
      }

      setInboundTransactions(inbounds);
      setOutboundTransactions(outbounds);
    } finally {
      setIsLoading(false);
    }
  }, [supabase, currentItem]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ==============================================================================
  // Ledger Calculation: Prior Transactions, Beginning Balance & Running Balance
  // ==============================================================================
  const {
    beginningBalance,
    ledgerRows,
    totalReceived,
    totalDispensed,
    endingBalance
  } = useMemo(() => {
    const { startDate, endDate } = periodInfo;
    const startMs = startDate.getTime();
    const endMs = endDate.getTime();

    // Transactions strictly before period start
    const priorIn = inboundTransactions
      .filter(t => t.date.getTime() < startMs)
      .reduce((sum, t) => sum + (Number(t.inQty) || 0), 0);

    const priorOut = outboundTransactions
      .filter(t => t.date.getTime() < startMs)
      .reduce((sum, t) => sum + (Number(t.outQty) || 0), 0);

    const hasPriorHistory = (priorIn > 0 || priorOut > 0);

    // Current period transactions
    const periodInbounds = inboundTransactions.filter(t => {
      const ms = t.date.getTime();
      return ms >= startMs && ms <= endMs;
    });

    const periodOutbounds = outboundTransactions.filter(t => {
      const ms = t.date.getTime();
      return ms >= startMs && ms <= endMs;
    });

    const periodTotalIn = periodInbounds.reduce((sum, t) => sum + (Number(t.inQty) || 0), 0);
    const periodTotalOut = periodOutbounds.reduce((sum, t) => sum + (Number(t.outQty) || 0), 0);

    // Live supplies total for this item
    const liveStockTotal = (supplies || [])
      .filter(s => s.master_id === currentItem?.id || (s.item_name && currentItem?.item_name && s.item_name.trim().toLowerCase() === currentItem.item_name.trim().toLowerCase()))
      .reduce((sum, s) => sum + (Number(s.item_amount) || 0), 0);

    // Calculate beginning balance
    let calculatedBeginning = 0;
    if (customBeginningBalance !== '' && !isNaN(Number(customBeginningBalance))) {
      calculatedBeginning = Number(customBeginningBalance);
    } else if (hasPriorHistory) {
      calculatedBeginning = Math.max(0, priorIn - priorOut);
    } else if (liveStockTotal > 0) {
      // Work backwards from current stock: Live = Beg + In - Out => Beg = Live - In + Out
      calculatedBeginning = Math.max(0, liveStockTotal - periodTotalIn + periodTotalOut);
    } else {
      calculatedBeginning = 0;
    }

    // Sort current period transactions chronologically
    const periodTx = [...periodInbounds, ...periodOutbounds].sort((a, b) => {
      const diff = a.date.getTime() - b.date.getTime();
      if (diff !== 0) return diff;
      // If same date, inbounds come before outbounds
      return a.type === 'inbound' ? -1 : 1;
    });

    // Compute running balance row by row
    let running = calculatedBeginning;
    const computedRows = periodTx.map(tx => {
      if (tx.type === 'inbound') {
        running += Number(tx.inQty) || 0;
      } else {
        running -= Number(tx.outQty) || 0;
      }
      return {
        ...tx,
        runningBalance: running
      };
    });

    return {
      beginningBalance: calculatedBeginning,
      ledgerRows: computedRows,
      totalReceived: periodTotalIn,
      totalDispensed: periodTotalOut,
      endingBalance: running
    };
  }, [
    periodInfo,
    inboundTransactions,
    outboundTransactions,
    supplies,
    currentItem,
    customBeginningBalance
  ]);

  // ==============================================================================
  // Action Handlers: Change Item, CSV Export, Print
  // ==============================================================================
  const handleItemChange = (e) => {
    const selectedId = e.target.value;
    const found = itemMaster.find(m => String(m.id) === String(selectedId));
    if (found) {
      setSelectedItemState(found);
      onSelectItem(found);
    }
  };

  const handleDownloadCSV = () => {
    const csvContent = generateMaterialLedgerCSV({
      item: currentItem,
      periodLabel: periodInfo.periodLabel,
      beginningDateThai: periodInfo.beginningDateThai,
      beginningBalance,
      isMonthly: periodMode === 'monthly',
      ledgerRows,
      totalReceived,
      totalDispensed,
      endingBalance,
      pageNumber
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const cleanName = (currentItem?.item_name || 'material').replace(/[/\\?%*:|"<>]/g, '_');
    link.setAttribute('download', `material_ledger_${cleanName}_${periodInfo.yearSuffix}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    window.print();
  };

  // Header Display Attributes from currentItem
  const category = currentItem?.category || 'วัสดุสิ้นเปลือง';
  const itemName = currentItem?.item_name || '-';
  const unitSize = currentItem?.unit_size || currentItem?.description || currentItem?.size || '-';
  const unit = currentItem?.unit || 'ชิ้น';
  const storageLocation = [
    currentItem?.cabinet ? `ตู้ ${currentItem.cabinet}` : '',
    currentItem?.shelf ? `ชั้น ${currentItem.shelf}` : '',
    currentItem?.row ? `แถว ${currentItem.row}` : ''
  ].filter(Boolean).join(' ') || currentItem?.location || currentItem?.storage_condition || '-';
  const itemCode = currentItem?.barcode || currentItem?.udi_di || (currentItem?.id ? String(currentItem.id).substring(0, 8) : '-');
  const maxStock = currentItem?.global_min_stock
    ? Number(currentItem.global_min_stock) * 3
    : (currentItem?.min_stock ? Number(currentItem.min_stock) * 3 : 0);
  const minStock = Number(currentItem?.min_stock ?? currentItem?.global_min_stock ?? 0);

  // Table styles
  const thStyle = {
    border: '1px solid #000000',
    padding: '6px 4px',
    textAlign: 'center',
    verticalAlign: 'middle',
    fontWeight: 'bold',
    fontSize: '13px',
    color: '#000000'
  };

  const tdStyle = {
    border: '1px solid #000000',
    padding: '5px 6px',
    verticalAlign: 'middle',
    fontSize: '12.5px',
    color: '#000000'
  };

  return (
    <div style={{ padding: '16px', backgroundColor: '#f8fafc', minHeight: '100vh', fontFamily: "'Sarabun', 'TH Sarabun New', sans-serif" }}>
      {/* Embedded Print Styling */}
      <style>{`
        @media print {
          @page {
            size: A4 landscape;
            margin: 10mm 12mm;
          }
          body * {
            visibility: hidden;
          }
          .material-ledger-sheet-container, .material-ledger-sheet-container * {
            visibility: visible;
          }
          .material-ledger-sheet-container {
            position: absolute;
            left: 0;
            top: 0;
            width: 100% !important;
            max-width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            box-shadow: none !important;
            border: none !important;
            background: #ffffff !important;
          }
          .no-print {
            display: none !important;
          }
          table {
            border-collapse: collapse !important;
            width: 100% !important;
          }
          th, td {
            border: 1px solid #000000 !important;
            color: #000000 !important;
          }
        }
      `}</style>

      {/* ========================================================================= */}
      {/* Controls Bar (Above Sheet, hidden in print mode) */}
      {/* ========================================================================= */}
      <div
        className="no-print"
        style={{
          maxWidth: '1100px',
          margin: '0 auto 20px auto',
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          padding: '16px 20px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
          border: '1px solid #e2e8f0'
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
          {/* Item Selector */}
          <div style={{ flex: '1 1 280px' }}>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 'bold', color: '#334155', marginBottom: '4px' }}>
              📦 เลือกชนิดวัสดุ:
            </label>
            <select
              aria-label="เลือกชนิดวัสดุ"
              value={currentItem?.id || ''}
              onChange={handleItemChange}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                fontSize: '14px',
                backgroundColor: '#f8fafc',
                color: '#1e293b'
              }}
            >
              {consumableItemOptions.map(m => (
                <option key={m.id} value={m.id}>
                  {m.item_name} {m.unit_size ? `(${m.unit_size})` : ''} - หน่วย: {m.unit || 'ชิ้น'}
                </option>
              ))}
            </select>
          </div>

          {/* Period Mode Toggle (Fiscal Year vs Monthly) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <div style={{ display: 'inline-flex', backgroundColor: '#e2e8f0', borderRadius: '8px', padding: '2px' }}>
              <button
                type="button"
                onClick={() => setPeriodMode('fiscal')}
                style={{
                  padding: '6px 14px',
                  borderRadius: '6px',
                  border: 'none',
                  fontSize: '13px',
                  fontWeight: periodMode === 'fiscal' ? 'bold' : 'normal',
                  backgroundColor: periodMode === 'fiscal' ? '#2563eb' : 'transparent',
                  color: periodMode === 'fiscal' ? '#ffffff' : '#475569',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                📅 ปีงบประมาณ
              </button>
              <button
                type="button"
                onClick={() => setPeriodMode('monthly')}
                style={{
                  padding: '6px 14px',
                  borderRadius: '6px',
                  border: 'none',
                  fontSize: '13px',
                  fontWeight: periodMode === 'monthly' ? 'bold' : 'normal',
                  backgroundColor: periodMode === 'monthly' ? '#2563eb' : 'transparent',
                  color: periodMode === 'monthly' ? '#ffffff' : '#475569',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                🗓️ เลือกรายเดือน
              </button>
            </div>

            {/* Fiscal Year Dropdown */}
            {periodMode === 'fiscal' ? (
              <select
                aria-label="เลือกปีงบประมาณ"
                value={selectedFiscalYear}
                onChange={(e) => setSelectedFiscalYear(Number(e.target.value))}
                style={{
                  padding: '7px 12px',
                  borderRadius: '8px',
                  border: '1px solid #cbd5e1',
                  fontSize: '13.5px',
                  backgroundColor: '#ffffff'
                }}
              >
                {fiscalYearOptions.map(y => (
                  <option key={y} value={y}>
                    ปีงบประมาณ {y} (1 ต.ค. {y - 1} - 30 ก.ย. {y})
                  </option>
                ))}
              </select>
            ) : (
              /* Month & Calendar Year Selectors */
              <div style={{ display: 'inline-flex', gap: '6px' }}>
                <select
                  aria-label="เลือกเดือน"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                  style={{
                    padding: '7px 10px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    fontSize: '13.5px',
                    backgroundColor: '#ffffff'
                  }}
                >
                  {THAI_MONTHS_FULL.map((mName, idx) => (
                    <option key={idx + 1} value={idx + 1}>
                      {mName}
                    </option>
                  ))}
                </select>
                <select
                  aria-label="เลือกปี พ.ศ."
                  value={selectedCalendarYear}
                  onChange={(e) => setSelectedCalendarYear(Number(e.target.value))}
                  style={{
                    padding: '7px 10px',
                    borderRadius: '8px',
                    border: '1px solid #cbd5e1',
                    fontSize: '13.5px',
                    backgroundColor: '#ffffff'
                  }}
                >
                  {calendarYearOptions.map(y => (
                    <option key={y} value={y}>
                      พ.ศ. {y}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              onClick={handleDownloadCSV}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 16px',
                borderRadius: '8px',
                border: '1px solid #10b981',
                backgroundColor: '#ecfdf5',
                color: '#065f46',
                fontWeight: 'bold',
                fontSize: '13.5px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              title="ส่งออกไฟล์ CSV พร้อม Header ภาษาไทย (UTF-8 BOM รองรับ Excel)"
            >
              📥 ดาวน์โหลด Excel / CSV
            </button>
            <button
              type="button"
              onClick={handlePrint}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 16px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: '#1e293b',
                color: '#ffffff',
                fontWeight: 'bold',
                fontSize: '13.5px',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              title="พิมพ์แบบฟอร์ม บัญชีวัสดุ มาตรฐานกระทรวงสาธารณสุข A4 แนวนอน"
            >
              🖨️ สั่งพิมพ์ / บันทึกเป็น PDF
            </button>
          </div>
        </div>

        {/* Secondary Bar: Page Number & Opening Balance Setting */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginTop: '12px', paddingTop: '10px', borderTop: '1px dashed #e2e8f0', fontSize: '13px', color: '#64748b' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span>แผ่นที่:</span>
            <input
              type="number"
              aria-label="แก้ไขเลขแผ่นที่"
              min="1"
              value={pageNumber}
              onChange={(e) => setPageNumber(Math.max(1, parseInt(e.target.value, 10) || 1))}
              style={{ width: '55px', padding: '3px 6px', borderRadius: '4px', border: '1px solid #cbd5e1', textAlign: 'center' }}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span>ยอดยกมาเริ่มต้น (ปรับแต่งได้):</span>
            <input
              type="number"
              aria-label="กำหนดยอดยกมาเริ่มต้น"
              placeholder={String(beginningBalance)}
              value={customBeginningBalance}
              onChange={(e) => setCustomBeginningBalance(e.target.value)}
              style={{ width: '80px', padding: '3px 6px', borderRadius: '4px', border: '1px solid #cbd5e1', textAlign: 'right' }}
            />
            {customBeginningBalance !== '' && (
              <button
                type="button"
                onClick={() => setCustomBeginningBalance('')}
                style={{ border: 'none', background: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '12px', padding: '0 4px' }}
                title="ล้างค่าที่กำหนดเองและใช้การคำนวณอัตโนมัติ"
              >
                ✕ รีเซ็ตเป็นค่าคำนวณ
              </button>
            )}
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px', color: '#0f766e', fontWeight: '500' }}>
            <span>📊 รอบระยะเวลาปัจจุบัน: {periodInfo.periodLabel}</span>
          </div>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* Official Government Form Container (บัญชีวัสดุ) */}
      {/* ========================================================================= */}
      <div
        className="material-ledger-sheet-container"
        style={{
          maxWidth: '1100px',
          margin: '0 auto',
          backgroundColor: '#ffffff',
          borderRadius: '4px',
          padding: '24px 32px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid #cbd5e1',
          color: '#000000',
          boxSizing: 'border-box'
        }}
      >
        {/* Document Centered Title */}
        <div style={{ textAlign: 'center', marginBottom: '16px' }}>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold', letterSpacing: '1px' }}>
            บัญชีวัสดุ
          </h1>
        </div>

        {/* Two-Column Government Form Header Grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1.2fr 1fr',
            gap: '12px 24px',
            marginBottom: '16px',
            fontSize: '13.5px',
            lineHeight: 1.6
          }}
        >
          {/* ================= Left Column ================= */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {/* แผ่นที่ (1) */}
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
              <span style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>แผ่นที่</span>
              <span style={{ borderBottom: '1px dotted #000000', flex: 1, minWidth: '40px', textAlign: 'center', fontWeight: '500' }}>
                {pageNumber}
              </span>
              <span style={{ fontSize: '11.5px', color: '#334155', whiteSpace: 'nowrap' }}>(1)</span>
            </div>

            {/* ประเภท (3) และ ชื่อหรือชนิดวัสดุ (4) */}
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', flex: '0 0 38%', gap: '4px' }}>
                <span style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>ประเภท</span>
                <span style={{ borderBottom: '1px dotted #000000', flex: 1, minWidth: '40px', textAlign: 'center' }}>
                  {category}
                </span>
                <span style={{ fontSize: '11.5px', color: '#334155', whiteSpace: 'nowrap' }}>(3)</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', flex: 1, gap: '4px' }}>
                <span style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>ชื่อหรือชนิดวัสดุ</span>
                <span style={{ borderBottom: '1px dotted #000000', flex: 1, minWidth: '60px', textAlign: 'center', fontWeight: 'bold' }}>
                  {itemName}
                </span>
                <span style={{ fontSize: '11.5px', color: '#334155', whiteSpace: 'nowrap' }}>(4)</span>
              </div>
            </div>

            {/* ขนาดหรือลักษณะ */}
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
              <span style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>ขนาดหรือลักษณะ</span>
              <span style={{ borderBottom: '1px dotted #000000', flex: 1, minWidth: '80px', textAlign: 'center' }}>
                {unitSize}
              </span>
            </div>

            {/* หน่วยที่นับ (6) และ ที่เก็บ */}
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', flex: '0 0 38%', gap: '4px' }}>
                <span style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>หน่วยที่นับ</span>
                <span style={{ borderBottom: '1px dotted #000000', flex: 1, minWidth: '40px', textAlign: 'center' }}>
                  {unit}
                </span>
                <span style={{ fontSize: '11.5px', color: '#334155', whiteSpace: 'nowrap' }}>(6)</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', flex: 1, gap: '4px' }}>
                <span style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>ที่เก็บ</span>
                <span style={{ borderBottom: '1px dotted #000000', flex: 1, minWidth: '60px', textAlign: 'center' }}>
                  {storageLocation}
                </span>
              </div>
            </div>
          </div>

          {/* ================= Right Column ================= */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {/* ส่วนราชการ [โรงพยาบาลฝาง] */}
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
              <span style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>ส่วนราชการ</span>
              <span style={{ borderBottom: '1px dotted #000000', flex: 1, minWidth: '80px', textAlign: 'center', fontWeight: 'bold' }}>
                โรงพยาบาลฝาง
              </span>
            </div>

            {/* หน่วยงาน (2) [กลุ่มงานทันตกรรม] */}
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
              <span style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>หน่วยงาน</span>
              <span style={{ borderBottom: '1px dotted #000000', flex: 1, minWidth: '80px', textAlign: 'center', fontWeight: '500' }}>
                กลุ่มงานทันตกรรม
              </span>
              <span style={{ fontSize: '11.5px', color: '#334155', whiteSpace: 'nowrap' }}>(2)</span>
            </div>

            {/* รหัส (5) */}
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
              <span style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>รหัส</span>
              <span style={{ borderBottom: '1px dotted #000000', flex: 1, minWidth: '80px', textAlign: 'center', fontFamily: 'monospace', fontWeight: 'bold' }}>
                {itemCode}
              </span>
              <span style={{ fontSize: '11.5px', color: '#334155', whiteSpace: 'nowrap' }}>(5)</span>
            </div>

            {/* จำนวนอย่างสูง (6) */}
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
              <span style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>จำนวนอย่างสูง</span>
              <span style={{ borderBottom: '1px dotted #000000', flex: 1, minWidth: '60px', textAlign: 'center', fontWeight: '500' }}>
                {maxStock}
              </span>
              <span style={{ fontSize: '11.5px', color: '#334155', whiteSpace: 'nowrap' }}>(6)</span>
            </div>

            {/* จำนวนอย่างต่ำ (7) */}
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
              <span style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>จำนวนอย่างต่ำ</span>
              <span style={{ borderBottom: '1px dotted #000000', flex: 1, minWidth: '60px', textAlign: 'center', fontWeight: '500' }}>
                {minStock}
              </span>
              <span style={{ fontSize: '11.5px', color: '#334155', whiteSpace: 'nowrap' }}>(7)</span>
            </div>
          </div>
        </div>

        {/* ========================================================================= */}
        {/* Ledger Table (2 Header Rows, Bordered, Matching Thai Government Spec) */}
        {/* ========================================================================= */}
        <div style={{ width: '100%', overflowX: 'auto' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              border: '1px solid #000000',
              fontFamily: "'Sarabun', 'TH Sarabun New', sans-serif"
            }}
          >
            <thead>
              {/* Header Row 1 */}
              <tr style={{ backgroundColor: '#fdfdfd' }}>
                <th rowSpan={2} style={{ ...thStyle, width: '95px' }}>
                  วัน เดือน ปี
                </th>
                <th rowSpan={2} style={{ ...thStyle, minWidth: '180px' }}>
                  รับจาก/จ่ายให้
                </th>
                <th rowSpan={2} style={{ ...thStyle, width: '125px' }}>
                  <div>เลขที่เอกสาร</div>
                  <div style={{ fontSize: '11px', fontWeight: 'normal', color: '#475569' }}>(8)</div>
                </th>
                <th rowSpan={2} style={{ ...thStyle, width: '90px' }}>
                  <div>ราคาต่อหน่วย</div>
                  <div>บาท</div>
                </th>
                <th colSpan={3} style={{ ...thStyle, width: '220px' }}>
                  จำนวน
                </th>
                <th rowSpan={2} style={{ ...thStyle, minWidth: '130px' }}>
                  หมายเหตุ
                </th>
              </tr>
              {/* Header Row 2 (Sub-columns under จำนวน) */}
              <tr style={{ backgroundColor: '#fdfdfd' }}>
                <th style={{ ...thStyle, width: '70px' }}>รับ</th>
                <th style={{ ...thStyle, width: '70px' }}>จ่าย</th>
                <th style={{ ...thStyle, width: '80px' }}>คงเหลือ</th>
              </tr>
            </thead>

            <tbody>
              {/* ================= Row 1: ยอดยกมา ================= */}
              <tr style={{ backgroundColor: '#ffffff' }}>
                <td style={{ ...tdStyle, textAlign: 'center', whiteSpace: 'nowrap' }}>
                  {periodInfo.beginningDateThai}
                </td>
                <td style={{ ...tdStyle, fontWeight: 'bold' }}>
                  ยอดยกมา
                </td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>-</td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>-</td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>-</td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>-</td>
                <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold' }}>
                  {Number(beginningBalance).toLocaleString()}
                </td>
                <td style={{ ...tdStyle, color: '#334155' }}>
                  {periodMode === 'monthly' ? 'ยอดยกมาจากเดือนก่อนหน้า' : 'ยอดยกมาจากปีก่อนหน้า'}
                </td>
              </tr>

              {/* ================= Chronological Transaction Rows ================= */}
              {ledgerRows.map((row, idx) => (
                <tr key={row.id || idx} style={{ backgroundColor: idx % 2 === 0 ? '#ffffff' : '#fcfcfd' }}>
                  <td style={{ ...tdStyle, textAlign: 'center', whiteSpace: 'nowrap' }}>
                    {formatThaiDate(row.date)}
                  </td>
                  <td style={tdStyle}>
                    {row.actorOrTarget}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'center', fontFamily: 'monospace' }}>
                    {row.docNumber || '-'}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>
                    {row.unitPrice ? Number(row.unitPrice).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: row.inQty ? 'bold' : 'normal', color: row.inQty ? '#15803d' : undefined }}>
                    {row.inQty ? Number(row.inQty).toLocaleString() : '-'}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: row.outQty ? 'bold' : 'normal', color: row.outQty ? '#b91c1c' : undefined }}>
                    {row.outQty ? Number(row.outQty).toLocaleString() : '-'}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold' }}>
                    {Number(row.runningBalance).toLocaleString()}
                  </td>
                  <td style={tdStyle}>
                    {row.note || '-'}
                  </td>
                </tr>
              ))}

              {/* Empty filler rows if table has few entries, preserving authentic government paper layout */}
              {ledgerRows.length < 8 && Array.from({ length: 8 - ledgerRows.length }).map((_, emptyIdx) => (
                <tr key={`empty-${emptyIdx}`} style={{ height: '24px', backgroundColor: '#ffffff' }}>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>&nbsp;</td>
                  <td style={tdStyle}>&nbsp;</td>
                  <td style={tdStyle}>&nbsp;</td>
                  <td style={tdStyle}>&nbsp;</td>
                  <td style={tdStyle}>&nbsp;</td>
                  <td style={tdStyle}>&nbsp;</td>
                  <td style={tdStyle}>&nbsp;</td>
                  <td style={tdStyle}>&nbsp;</td>
                </tr>
              ))}
            </tbody>

            {/* ================= Summary Footer Row ================= */}
            <tfoot>
              <tr style={{ backgroundColor: '#f8fafc', fontWeight: 'bold' }}>
                <td colSpan={4} style={{ ...tdStyle, textAlign: 'center', fontWeight: 'bold' }}>
                  รวมทั้งสิ้น
                </td>
                <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold', color: '#15803d' }}>
                  {totalReceived > 0 ? totalReceived.toLocaleString() : '-'}
                </td>
                <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold', color: '#b91c1c' }}>
                  {totalDispensed > 0 ? totalDispensed.toLocaleString() : '-'}
                </td>
                <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold', color: '#0f172a' }}>
                  {Number(endingBalance).toLocaleString()}
                </td>
                <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 'bold' }}>
                  คงเหลือยกไป
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MaterialLedgerSheet;
